import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import useMergeState from '../../../shared/hooks/useMergeState';
import { api } from '../../../shared/hooks/api';
import { ExtraFee, Product, ProductCustomerAddOn } from '../../../data/entities';
import { error, success } from '../../../utils/toast';
import { ProductsWrapper } from './Styled';
import { CreateProductTitleBar } from '../../../configs/products';
import TabNavigation from './TabBar';
import BackButton from '../../../shared/components/BackButton';
import Title from './Title';
import apiMethods from '../../../utils/api';
import CancelModal from '../../../shared/components/CancelModal';
import { WarningUnsavedChanges } from '../../../configs/warningProductModal';

const initialData: Partial<Product> = {
    name: '',
    is_active: true,
    quantity: 0,
    materials: [],
    customers: [],
    extra_fees: [],
};

const AUTO_POPULATE_EXTRA_FEES_CATEGORIES = ['Phuy', 'Thùng'];

const CreateProduct = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [validated, setValidated] = useState(false);
    const [duplicateData, setDuplicateData] = useMergeState(initialData);
    const [{ isCreating }, createProduct] = api.post('/products/');
    const [isModified, setIsModified] = useState(false);
    const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
    const [navigationPath, setNavigationPath] = useState("");

    useEffect(() => {
        if (location.state) {
            const newDuplicateData = {
                ...location.state.duplicateData,
                name: location.state.duplicateData.name + ' - Copy',
                quantity: 0,
            };
            setDuplicateData(newDuplicateData);
        }
    }, [location.state]);

    useEffect(() => {
        if (AUTO_POPULATE_EXTRA_FEES_CATEGORIES.includes(duplicateData.category)) {
            apiMethods
                .get(`/extrafees?is_active=true&page_size=${Number.MAX_SAFE_INTEGER}`, { category: duplicateData.category })
                .then((data: any) => {
                    const extra_fees = data.results.map((item: any) => ({
                        extra_fee: item,
                        quantity: 1,
                    }));
                    setDuplicateData({ extra_fees });
                });
        } else {
            setDuplicateData({ extra_fees: [] });
        }
    }, [duplicateData.category]);

    // Mark as modified when duplicateData changes
    useEffect(() => {
        if (duplicateData.name || 
            duplicateData.materials.length > 0 || 
            duplicateData.customers.length > 0 ||
            duplicateData.extra_fees.length > 0) {
            setIsModified(true);
        }
    }, [duplicateData]);

    // Add beforeunload event listener for browser navigation
    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (isModified) {
                e.preventDefault();
                e.returnValue = "";
                return "";
            }
        };

        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, [isModified]);

    const confirmNavigation = useCallback((path: string) => {
        if (isModified) {
            setNavigationPath(path);
            setShowUnsavedDialog(true);
            return false;
        }
        
        navigate(path);
        return true;
    }, [isModified, navigate]);

    const handleBackClick = () => {
        confirmNavigation("/products/");
    };

    const handleSaveAndNavigate = async () => {
        setShowUnsavedDialog(false);
        await handleSubmit(duplicateData);
    };

    const handleDontSaveAndNavigate = () => {
        setShowUnsavedDialog(false);
        navigate(navigationPath);
    };

    const handleContinueEditing = () => {
        setShowUnsavedDialog(false);
    };

    const handleSubmit = async (data: Product) => {
        const materials = duplicateData.materials.map((item: any) => ({
            ...item,
            id: item.materialProductId,
            material_id: item.id,
            quantity: item.quantity,
            provider_name: item.provider_name,
            category: item.category,
            note: item.note,
            category_note: item.category_note,
            specification: item.specification,
            measurement: item.measurement,
            excess_quantity: item.excess_quantity,
            excess_price: item.excess_price,
            total_measurement: item.total_measurement,
        }));

        const customers = duplicateData.customers.map((item: any) => ({
            ...item,
            customer_id: item.customer.id,
            addons: item.addons.map((productCustomerAddon: ProductCustomerAddOn) => ({
                ...productCustomerAddon,
                addon_id: productCustomerAddon.addon.id,
            })),
        }));

        const extra_fees = duplicateData.extra_fees.map((item: any) => ({
            ...item,
            extra_fee_id: item.extra_fee.id,
        }));

        const requestBody = {
            ...duplicateData,
            category_group_id: duplicateData.category_group?.id,
            materials,
            customers,
            extra_fees,
        };

        await createProduct(requestBody)
            .then((data: any) => {
                success('Thành Công', 'Tạo mới sản phẩm thành công');
                setIsModified(false);
                navigate(`/products/${data.id}`);
                return true;
            })
            .catch((err: any) => {
                error("Lỗi tạo sản phẩm", err);
                return false;
            });
    };

    const handleChangeProductData = (updates: any) => {
        setIsModified(true);
        setDuplicateData(updates);
    };

    return (
        <ProductsWrapper>
            <BackButton label="Trở về" onClick={handleBackClick} />
            <Title title={CreateProductTitleBar.title} setValidated={setValidated} submitForm={handleSubmit} isLoading={isCreating} />
            <TabNavigation validated={validated} mergeProduct={handleChangeProductData} productData={duplicateData} />

            <CancelModal
                open={showUnsavedDialog}
                handleClose={handleDontSaveAndNavigate}
                handleConfirm={handleSaveAndNavigate}
                handleCancel={handleContinueEditing}
                title={WarningUnsavedChanges.title}
                contentText={WarningUnsavedChanges.context}
                closeButtonText={WarningUnsavedChanges.closeButtonText}
                confirmButtonText={WarningUnsavedChanges.confirmButtonText}
                cancelButtonText={WarningUnsavedChanges.cancelButtonText}
                showCancelButton={true}
            />
        </ProductsWrapper>
    );
};

export default CreateProduct;
